import NextHead from '@/components/NextHead';
import groups from '@/pages/Colors/groups.json';
import {
  ColorsContent,
  ColorsHeader,
  ColorsWrapper,
  FlexRowStyled,
  GroupButton,
  GroupImageWrapper,
  GroupName,
} from '@/pages/Colors/styled';
import metas from '@/routes/meta';
import {
  Color,
  Container,
  FlexRow,
  H1,
  Image,
  Paragraph,
  Text,
  Wrapper,
  bffService,
} from '@thisisdevelopment/akzonobel-core';
import {
  CmsSectionType,
  CmsVideo,
  ColorCard,
  ColorWall,
  Page,
} from '@thisisdevelopment/heritage-core-library';
import groupBy from 'lodash/groupBy';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { fetchColors } from '../../../pkg/akzonobel-core/hooks/useColors';
import { pageRevalidate } from '../../../src/config';
import { getColorGroupId, useHashScroll } from '@/hooks/useHasScroll';

type OurColorsType = {
  title: string;
  headline: string;
  video_cover: number;
  video: number;
  sections: CmsSectionType[];
};

type SSRPageProps = {
  page: OurColorsType;
  colors: Color[];
};
const Colors = ({ page, colors }: SSRPageProps) => {
  const theme = useTheme();
  const router = useRouter();
  const [selectedGroup, setSelectedGroup] = useState<string>(groups[0].name);

  const colorsGrouped = useMemo(() => {
    const group = groups.find(({ name }) => name === selectedGroup);
    const colorGroups = groupBy(colors, group?.path);

    return Object.keys(colorGroups).map((groupName) => {
      const groupItem = group?.items.find(({ name }) => name === groupName);

      return {
        name: groupName,
        colors: colorGroups[groupName],
        description: groupItem?.description,
        headerImage: groupItem?.headerImage,
      };
    });
  }, [selectedGroup, colors]);

  const meta = metas.colors;

  /* Re-run hash scroll when selectedGroup changes so newly-mounted refs are found */
  const register = useHashScroll({ deps: [selectedGroup] });

  /* Auto-switch the active group tab when landing with a hash, e.g. /en/colours#pale-tones */
  useEffect(() => {
    const hash = router.asPath.split('#')[1];
    if (!hash) return;

    const matchedGroup = groups.find((group) =>
      group.items?.some((item) => getColorGroupId(item.name) === hash),
    );

    if (matchedGroup) {
      setSelectedGroup(matchedGroup.name);
    }
  }, [router.asPath]);

  return (
    <div>
      <NextHead meta={meta} />
      <Page title={page.title} headline={page.headline}>
        <FlexRowStyled padding="20px" justifyContent="center" alignItems="center">
          <Text color={theme.colors.brandGray400}>Organize by: </Text>
          <FlexRow>
            {groups.map((group) => (
              <GroupButton
                key={`GroupButton_${group.name}`}
                onClick={() => setSelectedGroup(group.name)}
                isActive={group.name === selectedGroup}
              >
                {group.name}
              </GroupButton>
            ))}
          </FlexRow>
        </FlexRowStyled>

        {selectedGroup !== 'colour wall' && (
          <Wrapper>
            {colorsGrouped.map(({ name, colors, description, headerImage }) => (
              <Fragment key={`ColorGroup_${name}`}>
                {headerImage && (
                  <GroupImageWrapper ref={register(name)}>
                    <Image
                      src={headerImage}
                      alt="headerImage"
                      width="1600"
                      height="400"
                      sizes="100vw"
                    />
                    <GroupName>{name}</GroupName>
                  </GroupImageWrapper>
                )}
                <ColorsWrapper as="section">
                  <ColorsHeader>
                    <H1>{name}</H1>
                    <Paragraph>{description}</Paragraph>
                  </ColorsHeader>
                  <ColorsContent>
                    {colors.map((color) => (
                      <ColorCard
                        mode="Color"
                        key={`Color_${color.colorCollectionColorId}`}
                        color={color}
                      />
                    ))}
                  </ColorsContent>
                </ColorsWrapper>
              </Fragment>
            ))}
          </Wrapper>
        )}
        {selectedGroup === 'colour wall' && (
          <Wrapper pt="15px" pb="40px">
            <Container>
              <Wrapper bgColor={theme.colors.white} padding="0 10px 10px 10px">
                <ColorWall colors={colors} />
              </Wrapper>
            </Container>
          </Wrapper>
        )}
        <CmsVideo coverId={page.video_cover} videoId={page.video} mb="50px" />
      </Page>
    </div>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const page = await bffService.getCmsItem('our_colours').then((res) => res.data.data[0]);
  const colors = await fetchColors('', { flip90AntiClockwise: true });

  return {
    props: { page, colors },
    revalidate: pageRevalidate,
  };
};

export default Colors;
